// Phase 15 Plan 09 Task 1 — TournamentCard (expandable card).
//
// Collapsed: tournament name (Heading 20px Fredoka) + cycle-end indicator
// (Body 16px Manrope) + current-round chip ("Round X of 5") + standings
// preview placeholder (top 3 — populated after expand from rounds[]).
//
// Expanded: triggers fetchTournamentById(tournament_id) once + caches the
// Tournament wire in component-local state. Renders RoundTabStrip
// (Plan 09 Task 3) + the selected round's pairings list. Tap pairing
// (with non-null match_id) → router.navigate('/match/${match_id}').
//
// Per UI-SPEC §"Tournament screen primary focal point": expansion is
// inline (no extra route). The card surface uses --color-surface-mid +
// 2px --color-surface-stroke + 8px radius, matching FriendInviteSuggestion.
//
// PHASE-15 DEFERRAL — the standings preview footer is wired against the
// participants[] payload that lands when expanded. While collapsed, we
// show your_score + your_rank from the TournamentPreview (already in the
// lobby payload) so the surface has substance without a fetch.

import { useStore } from '@nanostores/react'
import { type JSX, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import {
  fetchTournamentById,
  type RoundPairing,
  type Tournament,
  type TournamentPreview,
} from '../../net/tournaments.js'
import { useRouter } from '../../router/router.js'
import { $authBearer } from '../../stores/auth.js'
import { RoundTabStrip } from './RoundTabStrip.js'
import { TournamentStandingsList } from './TournamentStandingsList.js'

type Round = 1 | 2 | 3 | 4 | 5

interface TournamentCardProps {
  readonly preview: TournamentPreview
}

function formatCycleEnd(epochMs: number): string {
  // Body 16px Manrope (UI-SPEC) — yyyy-mm-dd is locale-neutral, swap for
  // Intl.DateTimeFormat once UI-SPEC pins the exact date format.
  const d = new Date(epochMs)
  const y = d.getUTCFullYear()
  const m = String(d.getUTCMonth() + 1).padStart(2, '0')
  const dd = String(d.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${dd}`
}

function clampRoundIndex(value: number): Round {
  if (value < 1) return 1
  if (value > 5) return 5
  return value as Round
}

export function TournamentCard(props: TournamentCardProps): JSX.Element {
  const { preview } = props
  const intl = useIntl()
  const [expanded, setExpanded] = useState(false)
  const [wire, setWire] = useState<Tournament | null>(null)
  const [fetching, setFetching] = useState(false)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [selectedRound, setSelectedRound] = useState<Round>(
    clampRoundIndex(preview.current_round || 1),
  )
  const router = useRouter()
  // WR-04: resolve the opponent against the caller's own uuid (not by picking
  // player_a unconditionally). Also pass callerUuid through to the standings
  // list so the "you" highlight renders.
  const bearer = useStore($authBearer)
  const callerUuid = bearer?.user_uuid ?? null

  async function handleToggle(): Promise<void> {
    if (expanded) {
      setExpanded(false)
      return
    }
    setExpanded(true)
    if (wire !== null || fetching) return
    setFetching(true)
    setFetchError(null)
    try {
      const result = await fetchTournamentById(preview.tournament_id)
      setWire(result)
      setSelectedRound(clampRoundIndex(result.current_round || 1))
    } catch {
      setFetchError('error')
    } finally {
      setFetching(false)
    }
  }

  const rounds: RoundPairing[] | null = wire ? wire.rounds : null
  const selectedRoundData: RoundPairing | null =
    rounds !== null ? (rounds[selectedRound - 1] ?? null) : null

  return (
    <article
      data-testid={`tournament-card-${preview.tournament_id}`}
      className="card"
      style={{
        width: '100%',
        maxWidth: '480px',
        // Folk active-cup card: warm plum-tinted gradient (House Plum),
        // borderless, soft warm shadow — mirrors the prototype's plum cup card.
        background:
          'linear-gradient(160deg, var(--color-card), color-mix(in srgb, var(--color-accent-plum) 14%, var(--color-card)))',
        borderRadius: 'var(--radius-card)',
        overflow: 'hidden',
        boxShadow: 'var(--shadow-card)',
      }}
    >
      <button
        type="button"
        data-testid={`tournament-card-toggle-${preview.tournament_id}`}
        onClick={() => {
          void handleToggle()
        }}
        aria-expanded={expanded ? 'true' : 'false'}
        aria-controls={`tournament-card-body-${preview.tournament_id}`}
        style={{
          width: '100%',
          padding: 'var(--space-4)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-2)',
          background: 'none',
          border: 'none',
          color: 'var(--color-text-primary)',
          textAlign: 'left',
          cursor: 'pointer',
        }}
      >
        <h3
          style={{
            margin: 0,
            fontFamily: 'var(--font-family-display)',
            fontWeight: 700,
            fontSize: 'var(--font-size-20)',
            color: 'var(--color-text-primary)',
          }}
        >
          {preview.name}
        </h3>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontFamily: 'var(--font-family-body)',
            fontWeight: 500,
            fontSize: 'var(--font-size-14)',
            color: 'var(--color-text-tertiary)',
          }}
        >
          <span data-testid={`tournament-card-cycle-${preview.tournament_id}`}>
            {formatCycleEnd(preview.cycle_end_ts)}
          </span>
          <span
            data-testid={`tournament-card-round-chip-${preview.tournament_id}`}
            className="ribbon yellow"
            style={{
              padding: '2px var(--space-3)',
              borderRadius: 'var(--radius-pill)',
              backgroundColor:
                'color-mix(in srgb, var(--color-accent-plum) 16%, var(--color-card))',
              color: 'var(--color-accent-plum-deep)',
              fontFamily: 'var(--font-family-display)',
              fontWeight: 700,
              fontSize: 'var(--font-size-12)',
              letterSpacing: '0.05em',
            }}
          >
            <FormattedMessage
              id="social.tournaments.round_progress"
              defaultMessage="Round {round} of 5"
              values={{ round: Math.max(1, preview.current_round) }}
            />
          </span>
        </div>
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-3)',
            alignItems: 'baseline',
            fontFamily: 'var(--font-family-body)',
            fontWeight: 500,
            fontSize: 'var(--font-size-14)',
            color: 'var(--color-text-secondary)',
          }}
        >
          <span style={{ fontFeatureSettings: '"tnum" 1' }}>
            <FormattedMessage
              id="social.tournaments.points"
              defaultMessage="{points} pts"
              values={{ points: preview.your_score }}
            />
          </span>
          <span style={{ fontFeatureSettings: '"tnum" 1' }}>
            #{preview.your_rank}
          </span>
          {preview.is_leader ? (
            <span
              role="img"
              aria-label={intl.formatMessage({
                id: 'social.tournaments.current_leader_aria',
              })}
              style={{
                padding: '2px var(--space-2)',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--color-cta-primary)',
                color: 'var(--color-inkwell)',
                fontFamily: 'var(--font-family-display)',
                fontWeight: 700,
                fontSize: 'var(--font-size-11)',
              }}
            >
              #1
            </span>
          ) : null}
        </div>
        {/* Folk mini-bracket: 5 Swiss rounds, plum bars filled to the current
            round (decorative — round progress is already conveyed by the chip). */}
        <div
          aria-hidden="true"
          style={{
            display: 'flex',
            gap: 'var(--space-1)',
            marginTop: 'var(--space-1)',
          }}
        >
          {[1, 2, 3, 4, 5].map((r) => (
            <span
              key={r}
              style={{
                flex: 1,
                height: '6px',
                borderRadius: 'var(--radius-pill)',
                backgroundColor:
                  r <= Math.max(1, preview.current_round)
                    ? 'var(--color-accent-plum)'
                    : 'var(--color-paper-3)',
              }}
            />
          ))}
        </div>
      </button>

      {expanded ? (
        <div
          id={`tournament-card-body-${preview.tournament_id}`}
          data-testid={`tournament-card-body-${preview.tournament_id}`}
          style={{
            padding: 'var(--space-4)',
            borderTop: '2px solid var(--color-surface-stroke)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-4)',
          }}
        >
          {fetching ? (
            <div
              role="status"
              aria-live="polite"
              style={{
                fontFamily: 'var(--font-family-body)',
                fontSize: 'var(--font-size-14)',
                color: 'var(--color-text-tertiary)',
              }}
            >
              <FormattedMessage
                id="social.tournaments.loading"
                defaultMessage="Loading…"
              />
            </div>
          ) : fetchError !== null ? (
            <div
              role="alert"
              style={{
                fontFamily: 'var(--font-family-body)',
                fontSize: 'var(--font-size-14)',
                color: 'var(--color-cta-destructive)',
              }}
            >
              <FormattedMessage
                id="social.tournaments.load_error"
                defaultMessage="Couldn't load this tournament. Try again."
              />
            </div>
          ) : wire !== null && rounds !== null ? (
            <>
              <RoundTabStrip
                rounds={rounds}
                selectedRound={selectedRound}
                onSelectRound={setSelectedRound}
              />
              <PairingsList
                round={selectedRoundData}
                callerUuid={callerUuid}
                onTap={(matchId) => router.navigate(`/match/${matchId}`)}
              />
              <TournamentStandingsList
                participants={wire.participants}
                callerUuid={callerUuid}
              />
            </>
          ) : null}
        </div>
      ) : null}
    </article>
  )
}

interface PairingsListProps {
  readonly round: RoundPairing | null
  readonly callerUuid: string | null
  readonly onTap: (matchId: string) => void
}

function PairingsList(props: PairingsListProps): JSX.Element {
  const { round, callerUuid, onTap } = props
  if (round === null) return <div />
  if (round.status === 'future' || round.pairings.length === 0) {
    return (
      <p
        style={{
          margin: 0,
          fontFamily: 'var(--font-family-body)',
          fontSize: 'var(--font-size-14)',
          color: 'var(--color-text-tertiary)',
        }}
      >
        <FormattedMessage
          id="social.tournaments.round_future"
          defaultMessage="Not yet paired."
        />
      </p>
    )
  }
  return (
    <ul
      data-testid={`pairings-list-r${round.round}`}
      style={{
        listStyle: 'none',
        margin: 0,
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
      }}
    >
      {round.pairings.map((p, idx) => {
        const isBye = p.player_b_uuid === null
        // WR-04: resolve opponent by callerUuid. If the caller IS player_a,
        // the opponent is player_b (and vice versa). Falling back to player_a
        // unconditionally (the old behaviour) meant a caller who happened to
        // BE player_a saw their own name as "opponent".
        let opponentName: string
        if (isBye) {
          opponentName = '—'
        } else if (callerUuid !== null && p.player_a_uuid === callerUuid) {
          opponentName =
            p.player_b_display_name ?? p.player_a_display_name ?? 'Opponent'
        } else if (callerUuid !== null && p.player_b_uuid === callerUuid) {
          opponentName =
            p.player_a_display_name ?? p.player_b_display_name ?? 'Opponent'
        } else {
          // Caller is neither player — observer view, pick player_a by convention.
          opponentName =
            p.player_a_display_name ?? p.player_b_display_name ?? 'Opponent'
        }
        const tappable = p.match_id !== null && !isBye
        const onClick = (): void => {
          if (tappable && p.match_id !== null) {
            onTap(p.match_id)
          }
        }
        const key = `${round.round}-${p.match_id ?? `bye-${String(idx)}`}`
        return (
          <li key={key}>
            <button
              type="button"
              onClick={onClick}
              disabled={!tappable}
              data-pairing-result={p.your_result ?? 'none'}
              style={{
                width: '100%',
                padding: 'var(--space-3)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: 'var(--color-paper-2)',
                borderRadius: 'var(--radius-lg)',
                boxShadow: 'var(--shadow-tile)',
                color: 'var(--color-text-primary)',
                fontFamily: 'var(--font-family-body)',
                fontWeight: 500,
                fontSize: 'var(--font-size-14)',
                cursor: tappable ? 'pointer' : 'default',
              }}
            >
              <span>{opponentName}</span>
              <span
                style={{
                  fontFamily: 'var(--font-family-display)',
                  fontWeight: 700,
                  fontSize: 'var(--font-size-12)',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                }}
              >
                {p.your_result === null ? (
                  '—'
                ) : (
                  <FormattedMessage
                    id={`social.tournaments.result.${p.your_result === 'not_yours' ? 'other' : p.your_result}`}
                    defaultMessage={p.your_result}
                  />
                )}
              </span>
            </button>
          </li>
        )
      })}
    </ul>
  )
}
